import React, { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";
import { Link } from "react-router-dom";
import Avatar from "../assets/AvatarProfile.png";
import { jobsEndpoint, authyEndpoint, profilesEndpoint} from "../constants";

const UsageProgress = ({ title, current, limit }) => {
    const percentage = limit === 'unlimited' || limit === 0 || typeof limit !== 'number' ? 0 : Math.round((current / limit) * 100);
    const limitDisplay = limit === 'unlimited' || typeof limit !== 'number' ? 'Unlimited' : limit;
    return (
        <div>
            <div className="flex justify-between mb-1">
                <span className="text-base font-medium text-gray-700">{title}</span>
                <span className="text-sm font-medium text-gray-700">{current} / {limitDisplay}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${percentage}%` }}></div>
            </div>
        </div>
    );
};

const Dashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeJobs: { count: 0, change: 0 },
    shortlisted: { count: 0, change: 0 },
    timeToHire: { days: 0, change: 0 },
  });
  const [pipelineData, setPipelineData] = useState([]);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(jobsEndpoint + "stats/", {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setStats({
          activeJobs: { count: data.active_jobs, change: 0 }, // Change calculation needed
          shortlisted: { count: data.shortlisted_candidates, change: 0 }, // Change calculation needed
          timeToHire: { days: data.avg_time_to_hire, change: 0 }, // Change calculation needed
        });
        setPipelineData(data.pipeline_breakdown);
      } catch (error) {
        console.error("Failed to fetch job stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  useEffect(() => {
    const fetchUsageData = async () => {
      setUsageLoading(true);
      try {
        const response = await fetch(authyEndpoint+"organisation/usage/", {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setUsageData(data);
      } catch (error) {
        console.error("Failed to fetch usage data:", error);
      } finally {
        setUsageLoading(false);
      }
    };

    fetchUsageData();
  }, [token]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(profilesEndpoint+"list_notifications/", {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setNotifications(data.results); // Assuming the API returns paginated results
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();
  }, [token]);

  const [pipelineMockData, setPipelineMockData] = useState([
    { stage: "Applied", count: 1286, percentage: 85 },
    { stage: "Shortlisted", count: 864, percentage: 65 },
    { stage: "Interviews", count: 624, percentage: 45 },
    { stage: "Offer Sent", count: 246, percentage: 25 },
    { stage: "Hired", count: 124, percentage: 15 },
  ]);

  const StatCard = ({ title, count, change, trend, suffix, lineColor, shadowClass }) => (
    <div className={`bg-white rounded-xl border border-gray-200 flex flex-col ${shadowClass || ''}`}>
      <div className="bg-gray-50 py-3 px-5 rounded-t-xl">
        <h3 className="text-md font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="flex items-center px-5 py-3 gap-8 border-t border-gray-200 rounded-md">
        <p className="text-3xl font-semibold text-gray-900">
          {typeof count === "number" ? count.toLocaleString() : count} 
          {suffix && <span className="text-lg font-normal text-gray-500">{suffix}</span>}
        </p>
        <div className={`flex text-sm ${change >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          <span>{change >= 0 ? "↑" : "↓"} {Math.abs(change)}%</span>
          <span className="text-gray-600 ml-1">vs last month</span>
        </div>
      </div>
      <div className="my-4 h-[60px] w-full">
        <svg className="w-full h-full" viewBox="0 0 200 60" fill="none">
          <path
            d={trend}
            stroke={lineColor}
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </div>
    </div>
  );

  const { user, loading: userLoading } = useUser();

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Welcome back, {user ? user.first_name || user.username : 'Guest'}
          </h1>
          <p className="text-sm text-gray-500">
            Here is a complete overview of your system.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {user && (
            <Link to="/profile" className="flex items-center space-x-2 text-gray-700 hover:text-gray-900">
              <img
                src={user.picture || Avatar}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span>{user.first_name || user.username}</span>
            </Link>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-6 mb-6">
        <StatCard
          title="Active Job Openings"
          count={stats.activeJobs.count}
          change={stats.activeJobs.change}
          trend="M0 45L20 35L40 40L60 30L80 35L100 25L120 30L140 20L160 25L180 15L200 20"
          lineColor="#19BFB7"
          shadowClass="shadow-[0_4px_24px_0_rgba(25,191,183,0.10)]"
        />
        <StatCard
          title="Shortlisted Candidates"
          count={stats.shortlisted.count}
          change={stats.shortlisted.change}
          trend="M0 20L20 25L40 20L60 25L80 30L100 25L120 30L140 35L160 40L180 45L200 50"
          lineColor="#cb6350ff"
          shadowClass="shadow-[0_4px_24px_0_rgba(23,172,165,0.10)]"
        />
        <StatCard
          title="Avg. time to hire"
          count={stats.timeToHire.days}
          change={stats.timeToHire.change}
          trend="M0 30L20 25L40 35L60 30L80 40L100 35L120 45L140 40L160 50L180 45L200 55"
          suffix="Days"
          lineColor="#149992"
          shadowClass="shadow-[0_4px_24px_0_rgba(20,153,146,0.10)]"
        />
      </div>

      {/* Plan Usage Section */}
      {usageLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 text-center">
            <p>Loading Plan Usage...</p>
        </div>
      ) : usageData && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Plan Usage (Plan: {usageData.plan})
            </h3>
            <div className="space-y-4">
                <UsageProgress 
                    title="Recruiters" 
                    current={usageData.recruiters.current} 
                    limit={usageData.recruiters.limit} 
                />
                <UsageProgress 
                    title="Active Jobs" 
                    current={usageData.active_jobs.current} 
                    limit={usageData.active_jobs.limit} 
                />
                <UsageProgress 
                    title="Candidates" 
                    current={usageData.candidates.current} 
                    limit={usageData.candidates.limit} 
                />
            </div>
        </div>
      )}

      {/* Notifications Section */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="bg-gray-50 py-3 px-5">
            <h3 className="text-lg font-semibold text-gray-900">
              Notifications
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <Link 
                  to={notification.redirect_url} 
                  key={notification.id} 
                  className={`block p-4 hover:bg-gray-50 ${
                    notification.is_flag ? 'bg-red-50 border-l-4 border-red-500' : ''
                  } ${!notification.read ? 'font-bold' : ''}`}
                >
                  <div className="flex items-center">
                    {notification.is_flag && <span className="mr-2">🚩</span>}
                    <p className="text-sm text-gray-800">{notification.verb}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(notification.timestamp).toLocaleString()}
                  </p>
                </Link>
              ))
            ) : (
              <p className="p-4 text-sm text-gray-500">You have no new notifications.</p>
            )}
          </div>
          <div className="px-5 py-4">
            <Link to="/notifications" className="text-sm font-semibold text-blue-600 hover:underline">
              View all notifications
            </Link>
          </div>
        </div>
      </div>

      {/* Scheduled Interviews and Pipeline Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Scheduled Interviews */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="bg-gray-50 py-3 px-5">
            <h3 className="text-lg font-semibold text-gray-900">
              Scheduled Interviews
            </h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-y border-gray-200 rounded-t-md">
            <h3 className="text-2xl text-gray-900 font-semibold">24</h3>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <span>↑ 8.6%</span>
              <span className="text-gray-600 ml-1">vs last 30 days</span>
            </div>
          </div>

          <div>
            <div className="px-5 py-3 flex items-center justify-between border-b border-gray-200">
              <div className="flex gap-3 items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-sm"
                />
                <h5 className="text-gray-500 font-semibold">candidate</h5>
              </div>
              <div>
                <h5 className="text-gray-500 font-semibold text-sm">
                  position applied
                </h5>
              </div>
            </div>

            {scheduledInterviews.map((interview) => (
              <div
                key={interview.id}
                className="flex items-center justify-between p-4 border-b border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <img
                    src={interview.avatar}
                    alt=""
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      {interview.name}
                    </p>
                    <p className="text-sm text-gray-500">{interview.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {interview.position}
                  </p>
                  <p className="text-sm text-gray-500">
                    {interview.date} at {interview.time}
                  </p>
                </div>
              </div>
            ))}

            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-md font-semibold text-gray-900">
                  Page <span>1</span> of <span>4</span>
                </p>
              </div>
              <div className="flex gap-5 items-center">
                <button className="text-sm text-gray-900 py-2 px-3 rounded-md cursor-pointer">
                  Previous
                </button>
                <button className="text-sm text-gray-900 py-2 px-3 rounded-md cursor-pointer">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hiring Pipeline Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="bg-gray-50 py-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">
              Hiring Pipeline Breakdown
            </h3>
          </div>

          <div className="h-96 grid grid-cols-5 gap-3 py-3 px-5 border-t rounded-t-md border-gray-200">
            {pipelineMockData.map((stage, index) => (
              <div
                key={stage.stage}
                className="border flex flex-col gap-4 justify-between rounded-md border-gray-200 p-2"
              >
                <div className="flex flex-col gap-2 text-sm mb-1">
                  <p className="text-gray-600 text-md font-semibold">
                    Step {index + 1}
                  </p>
                  <h3 className="text-gray-900 xl:text-sm text-xs font-semibold">
                    {stage.stage}
                  </h3>
                </div>
                <div className="relative h-40 rotate-180 bg-transparent overflow-hidden">
                  <div
                    className="h-full bg-[#19BFB7] rounded-lg"
                    style={{ height: `${stage.percentage}%` }}
                  />
                  <span className="font-medium absolute top-1 -translate-x-1/2 left-1/2 rotate-180 text-white">
                    {stage.count.toLocaleString()}
                  </span>
                </div>

                <div className="text-xs flex gap-3 flex-col text-gray-500 mt-1">
                  <h4>Dropout ratio</h4>
                  <div className="flex items-center gap-1 text-green-600 font-semibold">
                    <span className="text-gray-900">0%</span>
                    <span>↑ 8.6%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Shortlisted Resumes Section */}
      <div className="bg-white rounded-xl border border-gray-200 mt-10">
        <div className="bg-gray-50 py-3 px-5">
          <h3 className="text-lg font-semibold text-gray-900">
            Shortlisted Resumes
          </h3>
        </div>
        <div className="flex gap-3 items-end py-4 px-5 border-y border-gray-200 rounded-t-md">
          <h3 className="text-2xl text-gray-900 font-semibold">120</h3>
          <div className="flex items-center gap-1 text-sm text-emerald-600">
            <span>↑ 8.6%</span>
            <span className="text-gray-600 ml-1">vs last 30 days</span>
          </div>
        </div>

        <div>
          <div className="px-5 py-3 grid grid-cols-7 border-b border-gray-200">
            <div className="flex gap-3 items-center col-span-3">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-sm"
              />
              <h5 className="text-gray-500 font-semibold">candidate</h5>
            </div>

            <div className="grid grid-cols-4 col-span-4">
              <h5 className="text-gray-500 font-semibold text-sm">
                Position Applied
              </h5>
              <h5 className="text-gray-500 font-semibold text-sm">
                Experience
              </h5>
              <h5 className="text-gray-500 font-semibold text-sm">AI Score</h5>
              <h5 className="text-gray-500 font-semibold text-sm">Status</h5>
            </div>
          </div>

          {scheduledInterviews.map((interview) => (
            <div
              key={interview.id}
              className="grid grid-cols-7 items-center p-4 border-b border-gray-200"
            >
              <div className="flex items-center gap-3 col-span-3">
                <input type="checkbox" className="rounded border-gray-300" />
                <img
                  src={interview.avatar}
                  alt=""
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-medium text-gray-900">{interview.name}</p>
                  <p className="text-sm text-gray-500">{interview.email}</p>
                </div>
              </div>
              <div>
                <p className="font-medium text-sm text-gray-500">
                  {interview.position}
                </p>
              </div>
              <div>
                <p className="font-medium text-sm text-gray-500">
                  {interview.experience}
                </p>
              </div>
              <div>
                <p className="font-medium text-sm text-gray-500">
                  {interview.score}
                </p>
              </div>
              <div>
                <span className="font-medium text-center text-[12px] bg-green-100 border border-green-400 py-2 px-3 rounded-md text-green-600">
                  {interview.status}
                </span>
                <span className="text-gray-600 cursor-pointer text-xl font-bold ml-10">
                  &#8942;
                </span>
              </div>
            </div>
          ))}

          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-md font-semibold text-gray-900">
                Page <span>1</span> of <span>4</span>
              </p>
            </div>
            <div className="flex gap-5 items-center">
              <button className="text-sm text-gray-900 py-2 px-3 rounded-md cursor-pointer">
                  Previous
                </button>
                <button className="text-sm text-gray-900 py-2 px-3 rounded-md cursor-pointer">
                  Next
                </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
